"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Search, Eye, Package, Truck, CheckCircle, Clock, XCircle, Download, Edit3, Trash2, AlertTriangle } from "lucide-react"
import LoadingSpinner from "../../components/common/LoadingSpinner"
import toast from "react-hot-toast"
import { 
  collection, 
  query, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  where, 
  orderBy 
} from 'firebase/firestore'
import { db } from "../../firebase/config.js" 

// Types
export interface OrderItem {
  id: string
  name: string
  price: number
  quantity: number
  image?: string
}

export interface Order {
  id: string;
  firebaseDocId?: string;
  items: OrderItem[];
  total: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  paymentStatus: 'pending' | 'completed' | 'failed' | 'half_paid'; // <-- add "half_paid"
  paymentMethod?: string;
  codAdvanceAmount?: number; // <-- add this field
  shippingAddress: {
    name: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
  };
  createdAt: string;
  updatedAt?: string
}

// Firebase Orders Service
const ordersService = {
  // Get all orders
  async getOrders(): Promise<Order[]> {
    try {
      const ordersRef = collection(db, 'orders')
      const q = query(ordersRef, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)
      
      return snapshot.docs.map(doc => ({
        ...doc.data(),
        firebaseDocId: doc.id, // Store Firebase document ID
        id: doc.data().id // Keep your custom order ID
      })) as Order[]
    } catch (error) {
      console.error('Error getting orders:', error)
      throw error
    }
  },

  // Update order status using Firebase document ID
  async updateOrderStatus(orderId: string, status: string): Promise<void> {
    try {
      // First, find the document by the order ID field
      const ordersRef = collection(db, 'orders')
      const q = query(ordersRef, where('id', '==', orderId))
      const snapshot = await getDocs(q)
      
      if (snapshot.empty) {
        throw new Error(`Order with ID ${orderId} not found`)
      }
      
      // Get the Firebase document ID
      const docRef = snapshot.docs[0].ref
      
      // Update the document
      await updateDoc(docRef, {
        status: status,
        updatedAt: new Date().toISOString()
      })
      
      console.log(`Order ${orderId} status updated to ${status}`)
    } catch (error) {
      console.error('Error updating order status:', error)
      throw error
    }
  },

  // Alternative method: Update using Firebase document ID directly
  async updateOrderStatusByDocId(firebaseDocId: string, status: string): Promise<void> {
    try {
      const docRef = doc(db, 'orders', firebaseDocId)
      
      await updateDoc(docRef, {
        status: status,
        updatedAt: new Date().toISOString()
      })
      
      console.log(`Order document ${firebaseDocId} status updated to ${status}`)
    } catch (error) {
      console.error('Error updating order status:', error)
      throw error
    }
  },

  // Delete order using custom order ID
  async deleteOrder(orderId: string): Promise<void> {
    try {
      // Find the document by the order ID field
      const ordersRef = collection(db, 'orders')
      const q = query(ordersRef, where('id', '==', orderId))
      const snapshot = await getDocs(q)
      
      if (snapshot.empty) {
        throw new Error(`Order with ID ${orderId} not found`)
      }
      
      // Delete the document
      const docRef = snapshot.docs[0].ref
      await deleteDoc(docRef)
      
      console.log(`Order ${orderId} deleted successfully`)
    } catch (error) {
      console.error('Error deleting order:', error)
      throw error
    }
  },

  // Alternative: Delete using Firebase document ID directly
  async deleteOrderByDocId(firebaseDocId: string): Promise<void> {
    try {
      const docRef = doc(db, 'orders', firebaseDocId)
      await deleteDoc(docRef)
      
      console.log(`Order document ${firebaseDocId} deleted successfully`)
    } catch (error) {
      console.error('Error deleting order:', error)
      throw error
    }
  }
}

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([])
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [paymentFilter, setPaymentFilter] = useState("")
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [showOrderModal, setShowOrderModal] = useState(false)
  
  // Bulk update states
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set())
  const [bulkUpdateStatus, setBulkUpdateStatus] = useState("")
  const [showBulkUpdate, setShowBulkUpdate] = useState(false)
  const [isBulkUpdating, setIsBulkUpdating] = useState(false)
  
  // Bulk delete states
  const [showBulkDelete, setShowBulkDelete] = useState(false)
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState("")

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "confirmed", label: "Confirmed" },
    { value: "shipped", label: "Shipped" },
    { value: "delivered", label: "Delivered" },
    { value: "cancelled", label: "Cancelled" },
  ]

  const paymentOptions = [
    { value: "", label: "All Payments" },
    { value: "pending", label: "Pending" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ]

  useEffect(() => {
    loadOrders()
  }, [])

  useEffect(() => {
    filterOrders()
  }, [orders, searchQuery, statusFilter, paymentFilter])

  const loadOrders = async () => {
    try {
      setIsLoading(true)
      const ordersData = await ordersService.getOrders()
      setOrders(ordersData)
    } catch (error) {
      toast.error("Failed to load orders")
    } finally {
      setIsLoading(false)
    }
  }

  const filterOrders = () => {
    let filtered = [...orders]

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (order) =>
          order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
          order.shippingAddress.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          order.shippingAddress.phone.includes(searchQuery),
      )
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter((order) => order.status === statusFilter)
    }

    // Payment filter
    if (paymentFilter) {
      filtered = filtered.filter((order) => order.paymentStatus === paymentFilter)
    }

    setFilteredOrders(filtered)
  }

  const handleStatusUpdate = async (orderId: string, newStatus: Order["status"]) => {
    try {
      // Check if order exists in our local state first
      const orderExists = orders.find(order => order.id === orderId)
      if (!orderExists) {
        toast.error("Order not found")
        return
      }

      // Use the Firebase document ID if available, otherwise use the query method
      if (orderExists.firebaseDocId) {
        await ordersService.updateOrderStatusByDocId(orderExists.firebaseDocId, newStatus)
      } else {
        await ordersService.updateOrderStatus(orderId, newStatus)
      }
      
      toast.success("Order status updated successfully")
      loadOrders()
    } catch (error) {
      console.error("Error updating order status:", error)
      toast.error("Failed to update order status - Order may not exist")
    }
  }

  // Bulk selection handlers
  const handleSelectOrder = (orderId: string) => {
    const newSelected = new Set(selectedOrderIds)
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId)
    } else {
      newSelected.add(orderId)
    }
    setSelectedOrderIds(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedOrderIds.size === filteredOrders.length) {
      setSelectedOrderIds(new Set())
    } else {
      setSelectedOrderIds(new Set(filteredOrders.map(order => order.id)))
    }
  }

  // Helper to check if order is eligible for deletion
  function hasPaymentMethod(order: object): order is Order & { paymentMethod: string } {
    return typeof (order as Record<string, unknown>)["paymentMethod"] === "string";
  }

  const canDeleteOrder = (order: Order) => {
    if (!hasPaymentMethod(order)) return false;
    if (order.paymentMethod.toLowerCase() !== 'cod') return false;
    const orderTime = new Date(order.createdAt).getTime();
    const now = Date.now();
    return (now - orderTime) <= 2 * 60 * 60 * 1000; // 2 hours in ms
  };

  // Get deletable orders from selected
  const getDeletableSelectedOrders = () => {
    return Array.from(selectedOrderIds).filter(orderId => {
      const order = orders.find(o => o.id === orderId)
      return order && canDeleteOrder(order)
    })
  }

  // Bulk update handler
  const handleBulkStatusUpdate = async () => {
    if (selectedOrderIds.size === 0 || !bulkUpdateStatus) {
      toast.error("Please select orders and status to update")
      return
    }

    if (!window.confirm(`Update ${selectedOrderIds.size} orders to "${bulkUpdateStatus}" status?`)) {
      return
    }

    setIsBulkUpdating(true)
    let successCount = 0
    let errorCount = 0

    try {
      // Process orders in batches to avoid overwhelming Firebase
      const orderIds = Array.from(selectedOrderIds)
      
      for (const orderId of orderIds) {
        try {
          // Check if order exists in our local state first
          const orderExists = orders.find(order => order.id === orderId)
          if (!orderExists) {
            console.warn(`Order ${orderId} not found in local state, skipping`)
            errorCount++
            continue
          }

          // Use the Firebase document ID if available, otherwise use the query method
          if (orderExists.firebaseDocId) {
            await ordersService.updateOrderStatusByDocId(orderExists.firebaseDocId, bulkUpdateStatus as Order["status"])
          } else {
            await ordersService.updateOrderStatus(orderId, bulkUpdateStatus as Order["status"])
          }
          successCount++
        } catch (error) {
          console.error(`Failed to update order ${orderId}:`, error)
          errorCount++
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully updated ${successCount} orders`)
      }
      if (errorCount > 0) {
        toast.error(`Failed to update ${errorCount} orders`)
      }

      // Reset bulk update state
      setSelectedOrderIds(new Set())
      setBulkUpdateStatus("")
      setShowBulkUpdate(false)
      
      // Reload orders
      loadOrders()

    } catch (error) {
      toast.error("Bulk update failed")
    } finally {
      setIsBulkUpdating(false)
    }
  }

  // Single order delete handler
  const handleDeleteOrder = async (orderId: string) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) return;
    
    try {
      const orderExists = orders.find(order => order.id === orderId)
      if (!orderExists) {
        toast.error("Order not found")
        return
      }

      // Use the Firebase document ID if available, otherwise use the query method
      if (orderExists.firebaseDocId) {
        await ordersService.deleteOrderByDocId(orderExists.firebaseDocId);
      } else {
        await ordersService.deleteOrder(orderId);
      }
      
      toast.success('Order deleted successfully');
      loadOrders();
      setShowOrderModal(false);
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    const deletableOrders = getDeletableSelectedOrders()
    
    if (deletableOrders.length === 0) {
      toast.error("No eligible orders selected for deletion")
      return
    }

    if (deleteConfirmText !== `DELETE ${deletableOrders.length} ORDERS`) {
      toast.error("Please type the confirmation text exactly as shown")
      return
    }

    setIsBulkDeleting(true)
    let successCount = 0
    let errorCount = 0

    try {
      for (const orderId of deletableOrders) {
        try {
          const orderExists = orders.find(order => order.id === orderId)
          if (!orderExists) {
            console.warn(`Order ${orderId} not found in local state, skipping`)
            errorCount++
            continue
          }

          // Use the Firebase document ID if available, otherwise use the query method
          if (orderExists.firebaseDocId) {
            await ordersService.deleteOrderByDocId(orderExists.firebaseDocId)
          } else {
            await ordersService.deleteOrder(orderId)
          }
          successCount++
        } catch (error) {
          console.error(`Failed to delete order ${orderId}:`, error)
          errorCount++
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} orders`)
      }
      if (errorCount > 0) {
        toast.error(`Failed to delete ${errorCount} orders`)
      }

      // Reset states
      setSelectedOrderIds(new Set())
      setShowBulkDelete(false)
      setDeleteConfirmText("")
      
      // Reload orders
      loadOrders()

    } catch (error) {
      toast.error("Bulk delete failed")
    } finally {
      setIsBulkDeleting(false)
    }
  }

  // Selective delete for only deletable orders
  const handleSelectiveDelete = () => {
    const deletableOrders = getDeletableSelectedOrders()
    if (deletableOrders.length === 0) {
      toast.error("No eligible orders selected for deletion (COD orders within 2 hours only)")
      return
    }
    setShowBulkDelete(true)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="w-4 h-4 text-yellow-500" />
      case "confirmed":
        return <CheckCircle className="w-4 h-4 text-blue-500" />
      case "shipped":
        return <Truck className="w-4 h-4 text-purple-500" />
      case "delivered":
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case "cancelled":
        return <XCircle className="w-4 h-4 text-red-500" />
      default:
        return <Package className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800"
      case "confirmed":
        return "bg-blue-100 text-blue-800"
      case "shipped":
        return "bg-purple-100 text-purple-800"
      case "delivered":
        return "bg-green-100 text-green-800"
      case "cancelled":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const exportOrders = () => {
    const csvContent = [
      ["Order ID", "Customer", "Total", "Status", "Payment", "Date"].join(","),
      ...filteredOrders.map((order) =>
        [
          order.id,
          order.shippingAddress.name,
          order.total,
          order.status,
          order.paymentStatus,
          new Date(order.createdAt).toLocaleDateString(),
        ].join(","),
      ),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `orders_${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders Management</h1>
          <p className="text-gray-600">Track and manage customer orders</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              loadOrders()
            }}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <span>Refresh Orders</span>
          </button>
          <button
            onClick={exportOrders}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Package className="w-8 h-8 text-gray-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-semibold text-gray-900">{orders.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Clock className="w-8 h-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-semibold text-gray-900">
                {orders.filter((o) => o.status === "pending").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Truck className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Shipped</p>
              <p className="text-2xl font-semibold text-gray-900">
                {orders.filter((o) => o.status === "shipped").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Delivered</p>
              <p className="text-2xl font-semibold text-gray-900">
                {orders.filter((o) => o.status === "delivered").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <XCircle className="w-8 h-8 text-red-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Cancelled</p>
              <p className="text-2xl font-semibold text-gray-900">
                {orders.filter((o) => o.status === "cancelled").length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex items-center space-x-4">
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Payment Filter */}
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {paymentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Count and Bulk Actions */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {filteredOrders.length} of {orders.length} orders
            {selectedOrderIds.size > 0 && (
              <span className="ml-2 text-blue-600 font-medium">
                ({selectedOrderIds.size} selected)
              </span>
            )}
          </div>
          
          {selectedOrderIds.size > 0 && (
            <div className="flex space-x-2">
              <button
                onClick={() => setShowBulkUpdate(true)}
                className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
              >
                <Edit3 className="w-4 h-4" />
                <span>Bulk Update Status</span>
              </button>
              
              {getDeletableSelectedOrders().length > 0 && (
                <button
                  onClick={handleSelectiveDelete}
                  className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Selected ({getDeletableSelectedOrders().length})</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedOrderIds.size === filteredOrders.length && filteredOrders.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order.id} className={`hover:bg-gray-50 ${selectedOrderIds.has(order.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedOrderIds.has(order.id)}
                      onChange={() => handleSelectOrder(order.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">#{order.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{order.shippingAddress.name}</div>
                      <div className="text-sm text-gray-500">{order.shippingAddress.phone}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{order.items.length} items</div>
                    <div className="text-sm text-gray-500">
                      {order.items.reduce((sum, item) => sum + item.quantity, 0)} units
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">₹{order.total.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(order.status)}
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusUpdate(order.id, e.target.value as Order["status"])}
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${getStatusColor(order.status)}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        order.paymentStatus === "completed"
                          ? "bg-green-100 text-green-800"
                          : order.paymentStatus === "failed"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {order.paymentStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{new Date(order.createdAt).toLocaleDateString()}</div>
                    <div className="text-sm text-gray-500">{new Date(order.createdAt).toLocaleTimeString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedOrder(order)
                          setShowOrderModal(true)
                        }}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Order Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {canDeleteOrder(order) && (
                        <button
                          onClick={() => handleDeleteOrder(order.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Order"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* Bulk Update Modal */}
      {showBulkUpdate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Bulk Update Status</h2>
                <button
                  onClick={() => setShowBulkUpdate(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-gray-600">
                  Update status for {selectedOrderIds.size} selected orders
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Status
                  </label>
                  <select
                    value={bulkUpdateStatus}
                    onChange={(e) => setBulkUpdateStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Status</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowBulkUpdate(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkStatusUpdate}
                    disabled={!bulkUpdateStatus || isBulkUpdating}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isBulkUpdating ? "Updating..." : "Update Status"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <h2 className="text-xl font-bold text-gray-900">Delete Orders</h2>
                </div>
                <button
                  onClick={() => {
                    setShowBulkDelete(false)
                    setDeleteConfirmText("")
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Warning</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>You are about to permanently delete {getDeletableSelectedOrders().length} orders.</p>
                        <p className="mt-1">This action cannot be undone.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    Only COD orders placed within the last 2 hours can be deleted.
                  </p>
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    Type <span className="bg-gray-100 px-2 py-1 rounded font-mono">DELETE {getDeletableSelectedOrders().length} ORDERS</span> to confirm:
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder={`DELETE ${getDeletableSelectedOrders().length} ORDERS`}
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowBulkDelete(false)
                      setDeleteConfirmText("")
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={deleteConfirmText !== `DELETE ${getDeletableSelectedOrders().length} ORDERS` || isBulkDeleting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isBulkDeleting ? "Deleting..." : "Delete Orders"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Order Details</h2>
                <div className="flex items-center gap-2">
                  {canDeleteOrder(selectedOrder) && (
                    <button
                      onClick={() => handleDeleteOrder(selectedOrder.id)}
                      className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Order"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                  <button 
                    onClick={() => setShowOrderModal(false)} 
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Order Info */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Information</h3>
                    <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Order ID:</span>
                        <span className="font-medium">#{selectedOrder.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium">{new Date(selectedOrder.createdAt).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Status:</span>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedOrder.status)}`}
                        >
                          {selectedOrder.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Payment:</span>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            selectedOrder.paymentStatus === "completed"
                              ? "bg-green-100 text-green-800"
                              : selectedOrder.paymentStatus === "failed"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {selectedOrder.paymentStatus}
                        </span>
                      </div>
                      {selectedOrder.paymentMethod && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Payment Method:</span>
                          <span className="font-medium uppercase">{selectedOrder.paymentMethod}</span>
                        </div>
                      )}
                      {selectedOrder.paymentMethod?.toLowerCase() === "cod" && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">COD Advance Paid:</span>
                          <span className="font-medium text-blue-700">
                            {selectedOrder.codAdvanceAmount
                              ? `₹${selectedOrder.codAdvanceAmount} (${selectedOrder.paymentStatus === "half_paid" ? "Paid" : "Not Paid"})`
                              : "Not Paid"}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-3">
                        <span className="text-gray-600">Total:</span>
                        <span className="font-bold text-xl text-green-600">₹{selectedOrder.total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipping Address</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-medium text-lg">{selectedOrder.shippingAddress.name}</p>
                      <p className="text-gray-600 flex items-center mt-1">
                        <span className="font-medium">📞</span>
                        <span className="ml-2">{selectedOrder.shippingAddress.phone}</span>
                      </p>
                      <div className="mt-3 text-gray-700">
                        <p className="font-medium">📍 Address:</p>
                        <p className="mt-1">{selectedOrder.shippingAddress.address}</p>
                        <p className="mt-1">
                          {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state} - {selectedOrder.shippingAddress.pincode}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Delete Warning for Eligible Orders */}
                  {canDeleteOrder(selectedOrder) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex">
                        <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">Deletion Available</h3>
                          <p className="text-sm text-yellow-700 mt-1">
                            This COD order can be deleted as it was placed within the last 2 hours.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Order Items */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items ({selectedOrder.items.length})</h3>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {selectedOrder.items.map((item, index) => (
                      <div key={item.id || index} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex-shrink-0">
                          <img
                            src={item.image || "/api/placeholder/64/64"}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg border"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = "/api/placeholder/64/64";
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{item.name}</h4>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-sm text-gray-600">Qty: {item.quantity}</span>
                            <span className="text-sm font-medium text-gray-900">₹{item.price.toLocaleString()} each</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-lg text-gray-900">
                            ₹{(item.price * item.quantity).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Order Summary */}
                  <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Items:</span>
                        <span className="font-medium">{selectedOrder.items.reduce((sum, item) => sum + item.quantity, 0)} units</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">₹{selectedOrder.items.reduce((sum, item) => sum + (item.price * item.quantity), 0).toLocaleString()}</span>
                      </div>
                      <div className="border-t pt-2">
                        <div className="flex justify-between font-bold text-lg">
                          <span className="text-gray-900">Total:</span>
                          <span className="text-green-600">₹{selectedOrder.total.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminOrders